<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import tmMixin from '@/mixins/tmMixin'
import chatReportMixin from '@/mixins/chatReportMixin'

const TheTeaser = () =>
  import(
    /* webpackPrefetch: true,webpackChunkName: "TheTeaser" */ '@/components/TheTeaser'
  )
const TheChatWindow = () =>
  import(
    /* webpackPrefetch: true,webpackChunkName: "TheChatWindow" */ '@/components/chatbox/TheChatWindow'
  )
const TheLiveChat = () =>
  import(
    /* webpackPrefetch: true,webpackChunkName: "TheLiveChat" */ '@/components/TheLiveChat'
  )
const TheBot = () =>
  import(
    /* webpackPrefetch: true,webpackChunkName: "TheBot" */ '@/components/TheBot'
  )

const TheSatisfaction = () =>
  import(
    /* webpackPrefetch: true,webpackChunkName: "TheSatisfaction" */ '@/components/TheSatisfaction'
  )
const TheQueuing = () =>
  import(
    /* webpackPrefetch: true,webpackChunkName: "TheQueuing" */ '@/components/TheQueuing'
  )

const localCss = () =>
  import(/* webpackChunkName: "localCss" */ '@/assets/scss/app.scss')

// App component
export default {
  name: 'App',
  components: {
    TheTeaser,
    TheChatWindow,
    TheLiveChat,
    TheBot,
    TheSatisfaction,
    TheQueuing,
  },
  mixins: [tmMixin, chatReportMixin],
  data() {
    return {
      loading: true,
      development: process.env.NODE_ENV === 'development',
      localCss: Number(process.env.VUE_APP_LOCAL_CSS),
      visibilityTimeout: null,
    }
  },
  computed: {
    ...mapState({
      widget: (state) => state.Widget,
      widgetDisplayHideTimestamp: (state) =>
        state.DateTime.widgetDisplayHideTimestamp,
    }),
    ...mapGetters({
      getCss: 'Widget/getCss',
      widgetIsInactive: 'Widget/widgetIsInactive',
    }),
    /**
     * @description Return active component name
     * @return {String} component name
     */
    activeWidgetComponent() {
      if (!this.cssLoaded) {
        return
      }

      switch (this.widget.widgetState) {
        case this.$enum.WIDGET_STATE.TEASER:
          return 'TheTeaser'

        case this.$enum.WIDGET_STATE.FULL_FRAME:
          return 'TheChatWindow'

        default:
          return null
      }
    },
    conversationComponent() {
      switch (this.widget.sessionType) {
        case this.$enum.SESSION_TYPES.CHAT:
          return 'TheLiveChat'

        case this.$enum.SESSION_TYPES.BOT:
          return 'TheBot'

        case this.$enum.SESSION_TYPES.SATISFACTION:
          return 'TheSatisfaction'

        case this.$enum.SESSION_TYPES.QUEUING:
          return 'TheQueuing'

        default:
          return null
      }
    },
    cssLoaded() {
      return (
        this.localCss ||
        (this.widget.cssLoaded.teaser &&
          this.widget.cssLoaded.editor &&
          this.widget.cssLoaded.fullframe)
      )
    },
  },
  watch: {
    'widget.widgetState': 'restartWidgetHandler',
    widgetIsInactive: 'setupDisplayVisibilityProccess',
  },
  async created() {
    // Start time sync for the whole app
    this.startTimeSync()
    this.SET_HISTORY_ITEM()

    if (this.localCss) {
      localCss()
    }

    this.setupDisplayVisibilityProccess(true)

    await this.setConfigurationAndIdentity()

    this.loading = false
  },
  beforeDestroy() {
    clearTimeout(this.visibilityTimeout)
    this.clearTimeSync()
  },
  tm: {
    innso_widget_history_change(event) {
      console.debug('innso_widget_history_change', event)
      this.SET_HISTORY_ITEM(event.detail)
    },
  },
  methods: {
    ...mapActions({
      getWidgetConfiguration: 'Widget/getWidgetConfiguration',
      setInitialState: 'Widget/setInitialState',
      setWidgetDisplayVisibilityTime: 'DateTime/setWidgetDisplayVisibilityTime',
      startTimeSync: 'TimeSync/startTimeSync',
      clearTimeSync: 'TimeSync/clearTimeSync',
      reset: 'reset',
    }),
    ...mapMutations({
      UPDATE_WIDGET_STATE: 'Widget/UPDATE_WIDGET_STATE',
      UPDATE_CSS_LOADED: 'Widget/UPDATE_CSS_LOADED',
      UPDATE_SESSION_TYPE_IS_CHANGING: 'Widget/UPDATE_SESSION_TYPE_IS_CHANGING',
      SET_HISTORY_ITEM: 'History/SET_HISTORY_ITEM',
      RESET_WIDGET_HIDE_TIMESTAMP: 'DateTime/RESET_WIDGET_HIDE_TIMESTAMP',
    }),
    async setConfigurationAndIdentity(restore = false) {
      try {
        const firstLoad =
          this.widget.widgetState === this.$enum.WIDGET_STATE.INIT

        await this.getWidgetConfiguration(restore)

        if (firstLoad) {
          await this.$_chatReportMixin_changeWidgetStateAndReport(
            this.$enum.WIDGET_STATE[this.widget.css.initialDisplayMode],
          )
        }
      } catch (e) {
        console.error(e)
      }
    },
    /**
     * @description Clear store in dev mode
     */
    clearStorage() {
      localStorage.clear()
      sessionStorage.clear()
      location.reload()
    },
    async restartWidgetHandler(status) {
      if (status === this.$enum.WIDGET_STATE.INIT) {
        this.$_tmMixin_pushDataLayer({
          event: this.$enum.TM_EVENT.WIDGET_CLOSED,
        })

        await this.resetWidget()
      }
    },
    async resetWidget() {
      this.reset()
      // Init navigation history after reset
      this.SET_HISTORY_ITEM()
      // Fetch fresh configuration options
      await this.setConfigurationAndIdentity(true)
    },
    checkWidgetVisibility() {
      if (!this.widget.fullframeOptions.persistencyChatboxVisibility) {
        return
      }

      if (
        this.widgetIsInactive &&
        this.widgetDisplayHideTimestamp &&
        this.$dayjs().isAfter(this.$dayjs(this.widgetDisplayHideTimestamp))
      ) {
        this.RESET_WIDGET_HIDE_TIMESTAMP()
        this.setInitialState()
      }
    },
    initDisplayVisibilityTime() {
      if (!this.widgetDisplayHideTimestamp && this.widgetIsInactive) {
        this.setWidgetDisplayVisibilityTime()
      }
    },
    setVisibilityTimeout() {
      this.initDisplayVisibilityTime()
      if (!this.widgetDisplayHideTimestamp) {
        return
      }

      const delay = this.$dayjs(this.widgetDisplayHideTimestamp).diff(
        this.$dayjs(),
      )
      console.debug(`Widget visibility: timer setup with ${delay}ms delay`)

      this.visibilityTimeout = setTimeout(() => {
        console.debug(`Widget visibility: timer ended`)
        this.checkWidgetVisibility()
        this.visibilityTimeout = null
      }, delay)
    },
    setupDisplayVisibilityProccess(init) {
      if (init && !this.visibilityTimeout) {
        this.checkWidgetVisibility()
        this.setVisibilityTimeout()
      } else if (!init && this.visibilityTimeout) {
        console.debug('Widget visibility: remove timer')
        this.visibilityTimeout = clearTimeout(this.visibilityTimeout)
        this.RESET_WIDGET_HIDE_TIMESTAMP()
      }
    },
  },
}
</script>

<template>
  <div id="innso-chat-container">
    <template v-if="!localCss">
      <link
        v-if="getCss($enum.STYLESHEET_NAMES.TEASER)"
        :href="getCss($enum.STYLESHEET_NAMES.TEASER)"
        rel="stylesheet"
        type="text/css"
        @load="UPDATE_CSS_LOADED($enum.STYLESHEET_NAMES.TEASER)"
      />
      <link
        v-if="getCss($enum.STYLESHEET_NAMES.FULLFRAME)"
        :href="getCss($enum.STYLESHEET_NAMES.FULLFRAME)"
        rel="stylesheet"
        type="text/css"
        @load="UPDATE_CSS_LOADED($enum.STYLESHEET_NAMES.FULLFRAME)"
      />
      <link
        v-if="getCss($enum.STYLESHEET_NAMES.EDITOR)"
        :href="getCss($enum.STYLESHEET_NAMES.EDITOR)"
        rel="stylesheet"
        type="text/css"
        @load="UPDATE_CSS_LOADED($enum.STYLESHEET_NAMES.EDITOR)"
      />
    </template>
    <div
      v-if="development"
      style="position: fixed; top: 0; right: 0; z-index: 5000000"
    >
      <button type="button" @click.prevent="UPDATE_WIDGET_STATE('teaser')">
        show teaser
      </button>
      <button type="button" @click.prevent="clearStorage">
        Clear Local storage
      </button>
    </div>
    <transition name="teaser-transition" mode="out-in" appear>
      <component :is="activeWidgetComponent" v-if="!loading" />
    </transition>
    <component
      :is="conversationComponent"
      v-if="!loading"
      @hook:created="UPDATE_SESSION_TYPE_IS_CHANGING(false)"
    />
  </div>
</template>

<style lang="scss">
#innso-chat-container {
  all: initial;
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 100000;
  min-width: 0;
  min-height: 0;
  overflow: visible;
}
</style>
